import React, { useMemo, useState } from 'react';
import "../../css/dashboardStylings/DashboardGardenGridStyling.css";
import fallbackImage from '../../images/fallback/plantFallbackImage.png'

const DashboardGardenGrid = ({ gardenMatrix, plants, gardenManager = false, isMobile = false, background = true }) => {
    const [hoveredPlant, setHoveredPlant] = useState(null);

    function normalizeArray(dataString) {
        let array = dataString.trim().split("\n").map(row => row.split(",").map(Number));
        array = array.filter(row => row.some(value => value !== 0));
        let transposed = array[0].map((_, colIndex) => array.map(row => row[colIndex]));
        transposed = transposed.filter(col => col.some(value => value !== 0));
        return transposed[0].map((_, rowIndex) => transposed.map(col => col[rowIndex]));
    }

    // Function to build plant map from the plants data
    const buildPlantMap = (matrix, plantsData) => {
        if (!plantsData || plantsData.length === 0) {
            return matrix.map(row => row.map(() => null));
        }

        // Initialize empty plant map
        const newPlantMap = matrix.map(row => row.map(() => null));

        plantsData.forEach((plantEntry, plantIndex) => {
            const {
                grid_x,
                grid_y,
                custom_planting_width,
                custom_planting_height,
                display_color,
                plant
            } = plantEntry;

            const plantingWidth = custom_planting_width || plant.planting_width || 1;
            const plantingHeight = custom_planting_height || plant.planting_height || 1;

            // Place the plant across all its cells
            for (let r = 0; r < plantingHeight; r++) {
                for (let c = 0; c < plantingWidth; c++) {
                    const targetRow = grid_y + r;
                    const targetCol = grid_x + c;

                    // Check bounds
                    if (targetRow >= 0 && targetRow < matrix.length && targetCol >= 0 && targetCol < matrix[0].length) {
                        newPlantMap[targetRow][targetCol] = {
                            name: plant.plant_name,
                            image: plant.optimized_plant_image_url || fallbackImage,
                            color: display_color,
                            plantingWidth,
                            plantingHeight,
                            rootCell: r === 0 && c === 0, // Only top-left cell is root
                            plantData: plant,
                            plantIndex: plantIndex // Add unique identifier for hover tracking
                        };
                    } else {
                        console.warn(`Plant position out of bounds: [${targetRow}, ${targetCol}]`);
                    }
                }
            }
        });

        return newPlantMap;
    };

    //  Memoized so it doesn't run every render
    const normalizedMatrix = useMemo(
        () => normalizeArray(gardenMatrix),
        [gardenMatrix]
    );

    const plantMap = useMemo(
        () => buildPlantMap(normalizedMatrix, plants),
        [normalizedMatrix, plants]
    );

    let containerSize = 0;
    if (gardenManager && !isMobile) {
        containerSize = 500;
    } else {
        containerSize = 220;
    }
    const rows = normalizedMatrix.length || 1;
    const cols = normalizedMatrix[0]?.length || 1;
    const cellSize = Math.floor(containerSize / Math.max(rows, cols));

    // Function to get the background color for a cell
    const getCellBackgroundColor = (rowIndex, colIndex, value) => {
        if (value !== 1) return "transparent";

        const plant = plantMap[rowIndex]?.[colIndex];
        if (plant && plant.color) {
            // Check if this plant is being hovered
            const isHovered = hoveredPlant !== null && plant.plantIndex === hoveredPlant;

            if (isHovered) {
                // Brighten the color on hover - you can adjust this effect
                return plant.color + "CC"; // More opaque on hover
            }

            // Use the plant's assigned color with some transparency
            return plant.color + "80"; // Add 80 for 50% opacity
        }

        return "#6d4c41"; // Default soil color
    };

    // Handle plant hover
    const handlePlantHover = (plantIndex) => {
        if (gardenManager) {  // Only set hover state if gardenManager is true
            setHoveredPlant(plantIndex);
        }
    };

    const handlePlantLeave = () => {
        if (gardenManager) {  // Only clear hover state if gardenManager is true
            setHoveredPlant(null);
        }
    };

    return (
        <div className={`dashboard-garden-grid-container ${gardenManager ? 'hover-enabled' : ''}`}>
            <div
                className={`planter-garden-image ${!background ? 'no-background-export' : ''}`}
                style={{
                    width: containerSize,
                    height: containerSize,
                }}
            >
                <div
                    className="planter-garden-image-matrix"
                    style={{
                        gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
                        gridTemplateRows: `repeat(${rows}, ${cellSize}px)`,
                        width: `${cellSize * cols}px`,
                        height: `${cellSize * rows}px`,
                    }}
                >
                    {normalizedMatrix.map((row, rowIndex) =>
                        row.map((value, colIndex) => {
                            const bgColor = getCellBackgroundColor(rowIndex, colIndex, value);
                            const hasBackground = bgColor !== "transparent";
                            const plant = plantMap[rowIndex]?.[colIndex];
                            const isHovered = hoveredPlant !== null && plant?.plantIndex === hoveredPlant;

                            return (
                                <div
                                    key={`${rowIndex}-${colIndex}`}
                                    className={`garden-cell ${hasBackground ? "has-background" : ""} ${isHovered ? "cell-hovered" : ""}`}
                                    style={{
                                        backgroundColor: bgColor,
                                        width: `${cellSize}px`,
                                        height: `${cellSize}px`,
                                    }}
                                    data-has-background={hasBackground}
                                >
                                    {plantMap[rowIndex]?.[colIndex]?.rootCell && (
                                        <div
                                            className="dashboard-plant-image-container"
                                            style={{
                                                width: `${cellSize * (Number(plantMap[rowIndex][colIndex].plantingWidth) || 1)}px`,
                                                height: `${cellSize * (Number(plantMap[rowIndex][colIndex].plantingHeight) || 1)}px`,
                                            }}
                                            onMouseEnter={() => handlePlantHover(plantMap[rowIndex][colIndex].plantIndex)}
                                            onMouseLeave={handlePlantLeave}
                                        >
                                            <img
                                                src={plantMap[rowIndex][colIndex].image}
                                                alt={plantMap[rowIndex][colIndex].name}
                                                className="dashboard-plant-image"
                                                loading="lazy"
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default DashboardGardenGrid;